import { useEffect, useState } from "react";

import { api, type AuditEvent, type AuditStats } from "../lib/api.ts";

const KIND_COLORS: Record<string, string> = {
	tool_call: "bg-blue-500/15 text-blue-300 border-blue-500/30",
	chat_completion: "bg-purple-500/15 text-purple-300 border-purple-500/30",
	auth: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

export function Audit() {
	const [events, setEvents] = useState<AuditEvent[] | null>(null);
	const [stats, setStats] = useState<AuditStats | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [filter, setFilter] = useState<{ tools: boolean; chats: boolean }>({
		tools: true,
		chats: true,
	});

	useEffect(() => {
		const kinds: string[] = [];
		if (filter.tools) kinds.push("tool_call");
		if (filter.chats) kinds.push("chat_completion");

		(async () => {
			try {
				const [eventsRes, statsRes] = await Promise.all([
					api.audit({ kinds, limit: 200 }),
					api.auditStats(),
				]);
				setEvents(eventsRes.events);
				setStats(statsRes);
			} catch (err) {
				setError((err as Error).message);
			}
		})();
	}, [filter]);

	return (
		<div className="px-6 py-8">
			<div className="flex items-baseline justify-between mb-6">
				<div>
					<h1 className="text-xl font-semibold">Audit log</h1>
					<p className="text-sm text-zinc-500 mt-1">
						{stats
							? `${stats.totalEvents} events · ${stats.toolCalls} tool calls · ${stats.chatCompletions} completions`
							: "Loading…"}
					</p>
				</div>
				<div className="flex gap-3 text-sm">
					<label className="flex items-center gap-2 text-zinc-400 cursor-pointer">
						<input
							type="checkbox"
							checked={filter.tools}
							onChange={(e) => setFilter((f) => ({ ...f, tools: e.target.checked }))}
						/>
						tool calls
					</label>
					<label className="flex items-center gap-2 text-zinc-400 cursor-pointer">
						<input
							type="checkbox"
							checked={filter.chats}
							onChange={(e) => setFilter((f) => ({ ...f, chats: e.target.checked }))}
						/>
						completions
					</label>
				</div>
			</div>

			{error && (
				<div className="p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300 mb-4">
					{error}
				</div>
			)}

			{stats && stats.toolCallsByName.length > 0 && (
				<div className="mb-6 border border-zinc-800 rounded-md bg-zinc-900/40 p-4">
					<div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Tool usage</div>
					<table className="w-full text-xs">
						<thead className="text-zinc-500">
							<tr>
								<th className="text-left pb-1">Tool</th>
								<th className="text-right pb-1">Calls</th>
								<th className="text-right pb-1">Failures</th>
								<th className="text-right pb-1">Success rate</th>
							</tr>
						</thead>
						<tbody>
							{stats.toolCallsByName.map((t) => (
								<tr key={t.toolName} className="border-t border-zinc-800">
									<td className="py-1.5 font-mono">{t.toolName}</td>
									<td className="py-1.5 text-right">{t.count}</td>
									<td className={`py-1.5 text-right ${t.failures > 0 ? "text-red-400" : ""}`}>
										{t.failures}
									</td>
									<td className="py-1.5 text-right">
										{(((t.count - t.failures) / t.count) * 100).toFixed(0)}%
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{events !== null && events.length === 0 && !error && (
				<div className="text-center text-zinc-500 mt-16 text-sm">
					No audit events yet. Start a chat to populate this log.
				</div>
			)}

			<div className="space-y-1.5">
				{events?.map((e, i) => (
					<EventRow key={`${e.ts}-${i}`} e={e} />
				))}
			</div>
		</div>
	);
}

function EventRow({ e }: { e: AuditEvent }) {
	const [expanded, setExpanded] = useState(false);
	const colorClass = KIND_COLORS[e.kind] ?? "bg-zinc-500/15 text-zinc-400 border-zinc-500/30";
	const time = new Date(e.ts).toLocaleString();

	let summary: string;
	let canExpand = false;
	if (e.kind === "tool_call") {
		summary = `${e.toolName} · ${e.latencyMs}ms · ${(e.outputBytes / 1024).toFixed(1)} KB`;
		canExpand = true;
	} else if (e.kind === "chat_completion") {
		summary = `${e.model} · ${e.inputTokens}/${e.outputTokens} tokens · $${e.estCostUsd.toFixed(4)} · ${(e.latencyMs / 1000).toFixed(1)}s`;
	} else {
		summary = `${e.event}${e.detail ? ` — ${e.detail}` : ""}`;
	}

	return (
		<div className="rounded-md bg-zinc-900/40 border border-zinc-800 overflow-hidden">
			<button
				type="button"
				onClick={canExpand ? () => setExpanded(!expanded) : undefined}
				className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs ${canExpand ? "hover:bg-zinc-800/40 cursor-pointer" : "cursor-default"}`}
			>
				<span className={`px-2 py-0.5 rounded border ${colorClass} whitespace-nowrap`}>{e.kind}</span>
				{e.kind === "tool_call" && !e.ok && (
					<span className="text-red-400 font-medium">failed</span>
				)}
				<span className="text-zinc-300 truncate flex-1">{summary}</span>
				<span className="text-zinc-500 whitespace-nowrap">{time}</span>
				{canExpand && <span className="text-zinc-600 text-[10px]">{expanded ? "▼" : "▶"}</span>}
			</button>
			{expanded && e.kind === "tool_call" && (
				<div className="px-3 pb-3 pt-1 border-t border-zinc-800/60 space-y-2 text-[11px]">
					<details open>
						<summary className="cursor-pointer text-zinc-500 mb-1">input</summary>
						<pre className="bg-terminal/60 rounded p-2 overflow-x-auto text-zinc-300 whitespace-pre-wrap break-words">
							{e.inputPreview}
						</pre>
					</details>
					<details open>
						<summary className="cursor-pointer text-zinc-500 mb-1">output preview</summary>
						<pre className="bg-terminal/60 rounded p-2 overflow-x-auto text-zinc-300 whitespace-pre-wrap break-words max-h-64">
							{e.outputPreview || "(empty)"}
						</pre>
					</details>
				</div>
			)}
		</div>
	);
}
